import React, { useState } from 'react';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { ZoomIn, ZoomOut, Maximize2, MapPin, Compass } from 'lucide-react';
import type { DimensionalSpace, TerrainRegion, Landmark } from '../../types/spatialKnowledge';

interface IslandMapProps {
  space: DimensionalSpace;
  onRegionClick?: (region: TerrainRegion) => void;
  onLandmarkClick?: (landmark: Landmark) => void;
}

export const IslandMap: React.FC<IslandMapProps> = ({
  space,
  onRegionClick,
  onLandmarkClick
}) => {
  const [hoveredRegion, setHoveredRegion] = useState<string | null>(null);
  const [hoveredLandmark, setHoveredLandmark] = useState<string | null>(null);
  const [selectedRegion, setSelectedRegion] = useState<TerrainRegion | null>(null);

  const { bounds, regions, globalPaths, atmosphereColor } = space;
  const width = bounds.maxX - bounds.minX;
  const height = bounds.maxY - bounds.minY;

  const handleRegionClick = (region: TerrainRegion) => {
    setSelectedRegion(region);
    onRegionClick?.(region);
  };

  const handleLandmarkClick = (landmark: Landmark, e: React.MouseEvent) => {
    e.stopPropagation();
    onLandmarkClick?.(landmark);
  };

  // Convert polygon points to SVG path
  const polygonToPath = (points: Array<{ x: number; y: number }>) => {
    if (points.length === 0) return '';
    const pathData = points
      .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`)
      .join(' ') + ' Z';
    return pathData;
  };

  // Get color with elevation shading
  const getRegionColor = (region: TerrainRegion, isHovered: boolean) => {
    const baseColor = region.color;
    if (isHovered) {
      // Lighten on hover
      return `color-mix(in srgb, ${baseColor} 70%, white)`;
    }
    // Darken based on elevation
    const darkenFactor = Math.abs(region.elevation) / 200;
    return `color-mix(in srgb, ${baseColor} ${100 - darkenFactor * 30}%, black)`;
  };

  return (
    <div className="relative w-full h-full bg-gradient-to-b from-sky-200 to-sky-100 overflow-hidden shadow-2xl">
      <TransformWrapper
        initialScale={0.8}
        minScale={0.5}
        maxScale={3}
        centerOnInit
        wheel={{ step: 0.1 }}
      >
        {({ zoomIn, zoomOut, resetTransform }) => (
          <>
            {/* Map Controls */}
            <div className="absolute top-4 right-4 z-20 flex flex-col gap-2">
              <button
                onClick={() => zoomIn()}
                className="p-2 bg-[var(--bg-raised)]/90 hover:bg-[var(--bg-raised)] shadow-lg transition-colors"
                title="Zoom In"
              >
                <ZoomIn className="h-5 w-5 text-charcoal-700" />
              </button>
              <button
                onClick={() => zoomOut()}
                className="p-2 bg-[var(--bg-raised)]/90 hover:bg-[var(--bg-raised)] shadow-lg transition-colors"
                title="Zoom Out"
              >
                <ZoomOut className="h-5 w-5 text-charcoal-700" />
              </button>
              <button
                onClick={() => resetTransform()}
                className="p-2 bg-[var(--bg-raised)]/90 hover:bg-[var(--bg-raised)] shadow-lg transition-colors"
                title="Reset View"
              >
                <Maximize2 className="h-5 w-5 text-charcoal-700" />
              </button>
            </div>

            {/* Legend */}
            <div className="absolute top-4 left-4 z-20 bg-[var(--bg-raised)]/90 backdrop-blur shadow-lg p-3 max-w-xs">
              <div className="flex items-center gap-2 mb-2">
                <Compass className="h-5 w-5 text-primary-600" />
                <h3 className="font-bold text-sm text-charcoal-900">{space.name}</h3>
              </div>
              <p className="text-xs text-charcoal-600 mb-3">{space.description}</p>
              <div className="space-y-1 text-xs">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-sand-300 border border-sand-500"></div>
                  <span>Beach - Basics</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-[var(--green)] border border-[var(--green)]"></div>
                  <span>Forest - Analysis</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-[var(--surface)] border border-[var(--rule)]"></div>
                  <span>Mountain - Advanced</span>
                </div>
              </div>
            </div>

            {/* Info Panel */}
            {selectedRegion && (
              <div className="absolute bottom-4 left-4 z-20 bg-[var(--bg-raised)] shadow-xl p-4 max-w-md">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-bold text-lg text-charcoal-900">{selectedRegion.name}</h3>
                  <button
                    onClick={() => setSelectedRegion(null)}
                    className="text-charcoal-400 hover:text-charcoal-600"
                  >
                    ×
                  </button>
                </div>
                <p className="text-sm text-charcoal-600 mb-3">{selectedRegion.description}</p>
                <div className="flex items-center gap-2 text-xs text-charcoal-500 mb-3">
                  <MapPin className="h-4 w-4" />
                  <span>{selectedRegion.landmarks.length} landmarks to discover</span>
                </div>
                <div className="flex gap-2">
                  <span className="px-2 py-1 bg-primary-100 text-primary-700 rounded text-xs font-medium">
                    {selectedRegion.type}
                  </span>
                  <span className="px-2 py-1 bg-sage-100 text-sage-700 rounded text-xs font-medium">
                    Elevation: {selectedRegion.elevation}
                  </span>
                </div>
              </div>
            )}

            {/* The Map */}
            <TransformComponent
              wrapperClass="!w-full !h-full"
              contentClass="!w-full !h-full"
            >
              <svg
                viewBox={`${bounds.minX} ${bounds.minY} ${width} ${height}`}
                className="w-full h-full"
                style={{ background: atmosphereColor || '#87CEEB' }}
              >
                {/* Ocean/Background */}
                <rect
                  x={bounds.minX}
                  y={bounds.minY}
                  width={width}
                  height={height}
                  fill="url(#oceanGradient)"
                />

                {/* Gradients */}
                <defs>
                  <radialGradient id="oceanGradient">
                    <stop offset="0%" stopColor="#4a9eff" />
                    <stop offset="100%" stopColor="#1e5a8e" />
                  </radialGradient>
                  <filter id="shadow">
                    <feDropShadow dx="2" dy="2" stdDeviation="3" floodOpacity="0.3" />
                  </filter>
                </defs>

                {/* Paths */}
                {globalPaths.map((path) => (
                  <path
                    key={path.id}
                    d={polygonToPath(path.waypoints)}
                    stroke="#8b7355"
                    strokeWidth="3"
                    strokeDasharray={path.type === 'trail' ? '5,5' : undefined}
                    fill="none"
                    opacity={path.discovered ? 0.8 : 0.3}
                  />
                ))}

                {/* Terrain Regions */}
                {regions.map((region) => {
                  const isHovered = hoveredRegion === region.id;
                  return (
                    <g key={region.id}>
                      <path
                        d={polygonToPath(region.polygon)}
                        fill={getRegionColor(region, isHovered)}
                        stroke="#5a4a3a"
                        strokeWidth="2"
                        opacity={region.discovered ? 1 : 0.7}
                        filter="url(#shadow)"
                        onMouseEnter={() => setHoveredRegion(region.id)}
                        onMouseLeave={() => setHoveredRegion(null)}
                        onClick={() => handleRegionClick(region)}
                        style={{ cursor: 'pointer', transition: 'fill 0.2s' }}
                      />
                      
                      {/* Region Label */}
                      {isHovered && (
                        <text
                          x={region.polygon.reduce((sum, p) => sum + p.x, 0) / region.polygon.length}
                          y={region.polygon.reduce((sum, p) => sum + p.y, 0) / region.polygon.length}
                          textAnchor="middle"
                          fill="white"
                          fontSize="16"
                          fontWeight="bold"
                          stroke="#333"
                          strokeWidth="0.5"
                          style={{ pointerEvents: 'none' }}
                        >
                          {region.name}
                        </text>
                      )}
                    </g>
                  );
                })}

                {/* Landmarks */}
                {regions.flatMap((region) =>
                  region.landmarks.map((landmark) => {
                    const isHovered = hoveredLandmark === landmark.id;
                    const size = landmark.size === 'large' ? 30 : landmark.size === 'medium' ? 20 : 15;
                    
                    return (
                      <g key={landmark.id}>
                        {/* Landmark marker */}
                        <circle
                          cx={landmark.position.x}
                          cy={landmark.position.y}
                          r={size}
                          fill={landmark.color || '#d4af37'}
                          stroke="#8b6914"
                          strokeWidth="2"
                          opacity={landmark.discovered ? 1 : 0.5}
                          filter="url(#shadow)"
                          onMouseEnter={() => setHoveredLandmark(landmark.id)}
                          onMouseLeave={() => setHoveredLandmark(null)}
                          onClick={(e) => handleLandmarkClick(landmark, e)}
                          style={{ cursor: 'pointer', transition: 'r 0.2s' }}
                        />
                        
                        {/* Icon */}
                        <text
                          x={landmark.position.x}
                          y={landmark.position.y + 6}
                          textAnchor="middle"
                          fontSize={size * 0.8}
                          style={{ pointerEvents: 'none' }}
                        >
                          {landmark.icon}
                        </text>
                        
                        {/* Label on hover */}
                        {isHovered && (
                          <g>
                            <rect
                              x={landmark.position.x - 60}
                              y={landmark.position.y - size - 30}
                              width="120"
                              height="25"
                              fill="white"
                              stroke="#333"
                              strokeWidth="1"
                              rx="4"
                            />
                            <text
                              x={landmark.position.x}
                              y={landmark.position.y - size - 13}
                              textAnchor="middle"
                              fill="#333"
                              fontSize="12"
                              fontWeight="600"
                              style={{ pointerEvents: 'none' }}
                            >
                              {landmark.name}
                            </text>
                          </g>
                        )}
                      </g>
                    );
                  })
                )}
              </svg>
            </TransformComponent>
          </>
        )}
      </TransformWrapper>
    </div>
  );
};

